import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaClock, FaCheckCircle, FaFire, FaTruck, FaTimesCircle, FaArrowLeft } from 'react-icons/fa';
import './OrderTracking.css';

const DELIVERY_STATUS_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: <FaClock /> },
  { key: 'confirmed', label: 'Confirmed', icon: <FaCheckCircle /> },
  { key: 'preparing', label: 'Preparing', icon: <FaFire /> },
  { key: 'ready', label: 'Out for Delivery', icon: <FaTruck /> },
  { key: 'delivered', label: 'Delivered', icon: <FaCheckCircle /> },
];
const TAKEAWAY_STATUS_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: <FaClock /> },
  { key: 'confirmed', label: 'Confirmed', icon: <FaCheckCircle /> },
  { key: 'preparing', label: 'Preparing', icon: <FaFire /> },
  { key: 'ready', label: 'Ready for Pickup', icon: <FaTruck /> },
  { key: 'delivered', label: 'Completed', icon: <FaCheckCircle /> },
];

const STATUS_INDEX = {
  pending: 0,
  confirmed: 1,
  preparing: 2,
  ready: 3,
  delivered: 4,
  cancelled: 4,
};

const PAYMENT_LABELS = {
  card: 'Card',
  revolut: 'Revolut',
  cash_on_delivery: 'Cash on Delivery',
  bank_transfer: 'Bank Transfer',
};

const money = (value) => `€${Number(value || 0).toFixed(2)}`;

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const fetchOrder = async () => {
      try {
        const res = await axios.get(`http://localhost:5000/api/orders/${orderId}`);
        if (!mounted) return;
        if (res.data?.success) {
          setOrder(res.data.data);
          setError('');
        }
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.message || 'Unable to load order tracking right now.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOrder();
    const id = setInterval(fetchOrder, 15000);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [orderId]);

  const statusIndex = useMemo(() => STATUS_INDEX[order?.status] ?? 0, [order?.status]);

  if (loading) {
    return (
      <div className="track-page">
        <div className="track-loading">Loading your order...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="track-page">
        <div className="track-card">
          <h1 className="track-title">Track Order</h1>
          <p className="track-error">{error || 'Order not found.'}</p>
          <button className="btn-outline track-back-btn" onClick={() => navigate('/')}>
            <FaArrowLeft />
            <span>Back to Home</span>
          </button>
        </div>
      </div>
    );
  }

  const isCancelled = order.status === 'cancelled';
  const isTakeaway = order.orderType === 'takeaway';
  const statusSteps = isTakeaway ? TAKEAWAY_STATUS_STEPS : DELIVERY_STATUS_STEPS;

  return (
    <div className="track-page">
      <div className="track-card">
        <div className="track-header">
          <div>
            <h1 className="track-title">Track Your Order</h1>
            <p className="track-subtitle">Live updates refresh every 15 seconds</p>
          </div>
          <button className="btn-outline track-back-btn" onClick={() => navigate('/')}>
            <FaArrowLeft />
            <span>Home</span>
          </button>
        </div>

        <div className="track-order-meta">
          <span><strong>Order ID:</strong> #{order._id.slice(-8).toUpperCase()}</span>
          <span><strong>Status:</strong> {isCancelled ? 'Rejected' : order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
          <span><strong>Total:</strong> {money(order.total)}</span>
        </div>

        <div className="track-timeline">
          {statusSteps.map((step, idx) => {
            const isDone = !isCancelled && idx < statusIndex;
            const isCurrent = !isCancelled && idx === statusIndex;
            const isLocked = !isDone && !isCurrent;

            return (
              <div
                key={step.key}
                className={`track-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''} ${isLocked ? 'locked' : ''}`}
              >
                <div className="track-step-icon">{step.icon}</div>
                <span className="track-step-label">{step.label}</span>
              </div>
            );
          })}
          {isCancelled && (
            <div className="track-step cancelled current">
              <div className="track-step-icon"><FaTimesCircle /></div>
              <span className="track-step-label">Rejected</span>
            </div>
          )}
        </div>

        {order.adminNotes && (
          <div className="track-admin-note">
            <strong>Update from restaurant:</strong> {order.adminNotes}
          </div>
        )}

        <div className="track-summary">
          <h2>Order Summary</h2>
          <div className="track-items">
            {order.items?.map((item, index) => (
              <div className="track-item" key={`${item.name}-${index}`}>
                <span>{item.name} x{item.quantity}</span>
                <span>{money(item.price * item.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="track-total-row"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          <div className="track-total-row"><span>Delivery</span><span>{money(order.deliveryFee)}</span></div>
          {!!order.discount && <div className="track-total-row discount"><span>Discount</span><span>-{money(order.discount)}</span></div>}
          <div className="track-total-row grand"><span>Total</span><span>{money(order.total)}</span></div>
          <div className="track-extra">
            <span><strong>Order Type:</strong> {isTakeaway ? 'Takeaway' : 'Delivery'}</span>
            <span><strong>Payment:</strong> {PAYMENT_LABELS[order.paymentMethod] || order.paymentMethod}</span>
            {isTakeaway
              ? <span><strong>Pickup:</strong> Collect from restaurant once status is ready.</span>
              : <span><strong>Delivery:</strong> {order.deliveryAddress?.street}, {order.deliveryAddress?.city}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
